import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from "react-router-dom";
import ChatBox from '../components/channel/ChatBox';
import ChannelConnection from '../js/connection/ChannelConnection';
import useHoloXRPageTitle from '../js/utils/useHoloXRPageTitle';
import liveswitch from 'fm.liveswitch';
import ChannelToolbar, { SidePanel } from '../components/channel/ChannelToolbar';
import ChannelValidationPage from '../components/channel/ChannelValidationPage';
import ChannelBanner from '../components/channel/ChannelBanner';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHandPaper, faCheck, faBan, faTh, faUser, faTimes, faCommentDots } from '@fortawesome/free-solid-svg-icons';
import ThumbnailPanel from "../components/channel/ThumbnailPanel";
import RootOverflowHandler from '../components/channel/RootOverflowHandler';
import SFULayoutOverlay from '../components/channel/SFULayoutOverlay';
import EmojiReactionLayer from '../components/channel/EmojiReactionLayer';
import LiveCaptions from '../components/channel/LiveCaptions';
import TileChromeLayer from '../components/channel/TileChromeLayer';
import FocusFilmstrip from '../components/channel/FocusFilmstrip';
import { playJoinTone, playLeaveTone } from '../js/audio/MeetingTones';
import {
    ChatMessage,
    createChatMessage,
    isUserChatMessage,
} from '../js/connection/ChatMessage';

export type LayoutMode = 'grid' | 'cover';

export default function Channel() {
    useHoloXRPageTitle('Channel');

    const connection = useRef(new ChannelConnection());
    const [, setSearchParams] = useSearchParams();
    const [localMedia, setLocalMedia] = useState<liveswitch.LocalMedia>();
    const [sidePanel, setSidePanel] = useState<SidePanel>(window.innerWidth >= 1024 ? 'chat' : null);
    const [isMobile, setMobile] = useState<boolean>(window.innerWidth < 768);
    const [controlRequest, setControlRequest] = useState<{ fromId: string; fromAlias: string } | null>(null);
    const [layoutMode, setLayoutMode] = useState<LayoutMode>('grid');
    const [raiseHandNotifs, setRaiseHandNotifs] = useState<{ id: number; alias: string }[]>([]);
    const [captionsEnabled, setCaptionsEnabled] = useState(false);
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const [unreadMessages, setUnreadMessages] = useState(0);
    const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
    const [chatNotifs, setChatNotifs] = useState<ChatMessage[]>([]);
    const notifCounter = useRef(0);
    const chatMessageCounter = useRef(0);
    const sidePanelRef = useRef<SidePanel>(sidePanel);
    const meetingStartTime = useRef(new Date());

    useEffect(() => {
        function handleResize() { setMobile(window.innerWidth < 768); }
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        const onControlRequest = (request: { fromId: string; fromAlias: string; data: any }) => {
            setControlRequest({ fromId: request.fromId, fromAlias: request.data.requestingUserAlias });
        };

        const onRaiseHand = ({ alias }: { alias: string }) => {
            const id = notifCounter.current++;
            setRaiseHandNotifs(prev => [...prev, { id, alias }]);
            setTimeout(() => {
                setRaiseHandNotifs(prev => prev.filter(n => n.id !== id));
            }, 8000);
        };

        const onConnectionOpen = () => { try { playJoinTone(); } catch {} };
        const onConnectionClose = () => { try { playLeaveTone(); } catch {} };

        const onMuteAll = ({ fromAlias }: { fromAlias: string }) => {
            const id = notifCounter.current++;
            const alias = fromAlias || 'Host';
            setRaiseHandNotifs(prev => [...prev, { id, alias: `${alias} asked everyone to mute` }]);
            setTimeout(() => {
                setRaiseHandNotifs(prev => prev.filter(n => n.id !== id));
            }, 5000);
        };

        const conn = connection.current;
        conn.events.addListener("onrequestcontrol", onControlRequest);
        conn.events.addListener("onraisehand", onRaiseHand);
        conn.events.addListener("onconnectionopen", onConnectionOpen);
        conn.events.addListener("onconnectionclose", onConnectionClose);
        conn.events.addListener("onmuteall", onMuteAll);

        return () => {
            conn.events.removeListener("onrequestcontrol", onControlRequest);
            conn.events.removeListener("onraisehand", onRaiseHand);
            conn.events.removeListener("onconnectionopen", onConnectionOpen);
            conn.events.removeListener("onconnectionclose", onConnectionClose);
            conn.events.removeListener("onmuteall", onMuteAll);
        };
    }, []);

    // Store chat history at the call level so closing the panel does not
    // unmount and discard messages.
    useEffect(() => {
        const conn = connection.current;
        const onMessage = ({ user, message }: { user: string; message: string }) => {
            if (!isUserChatMessage(message)) return;

            const selfAlias = conn.channel?.getUserAlias?.() ?? '';
            const chatMessage = createChatMessage(
                `chat-${Date.now()}-${chatMessageCounter.current++}`,
                user,
                message,
                selfAlias
            );
            setChatMessages(previous => [...previous.slice(-499), chatMessage]);

            if (sidePanelRef.current !== 'chat') {
                setUnreadMessages(count => count + 1);
                setChatNotifs(previous => [...previous.slice(-2), chatMessage]);
                window.setTimeout(() => {
                    setChatNotifs(previous =>
                        previous.filter(notification => notification.id !== chatMessage.id)
                    );
                }, 6500);
            }
        };
        conn.events.addListener("onmessage", onMessage);
        return () => conn.events.removeListener("onmessage", onMessage);
    }, []);

    useEffect(() => {
        sidePanelRef.current = sidePanel;
        if (sidePanel === 'chat') {
            setUnreadMessages(0);
            setChatNotifs([]);
        }
    }, [sidePanel]);

    useEffect(() => {
        if (localMedia) {
            setSearchParams({});
            meetingStartTime.current = new Date();
            connection.current.openConnection(localMedia);
        }
        return () => {
            if (localMedia) {
                connection.current.leaveChannel();
            }
        };
    }, [setSearchParams, localMedia]);

    if (!localMedia) {
        return <ChannelValidationPage connection={connection.current} onConfirm={(lm) => setLocalMedia(lm)} />;
    }

    function handleMuteAll() {
        try {
            connection.current.messager.sendChatMessage(
                JSON.stringify({ type: 'mute_all', fromAlias: connection.current.channel.getUserAlias() })
            );
        } catch {}
    }

    const videoAreaClass = `video-grid${layoutMode === 'cover' ? ' layout-cover' : ''}`;

    const sidePanelContent = sidePanel === 'chat' ? (
        <ChatBox
            connection={connection.current}
            messages={chatMessages}
            onClose={() => setSidePanel(null)}
        />
    ) : sidePanel === 'participants' ? (
        <ThumbnailPanel
            connections={connection.current.sfuConnection.getAllConnections()}
            isMobileView={isMobile}
            onClose={() => setSidePanel(null)}
        />
    ) : null;

    return (
        <>
            <RootOverflowHandler connection={connection.current} />
            <div className="channel-root">
                <ChannelBanner connection={connection.current} />

                <div className="channel-body">
                    <div className="channel-stage">
                        <div className="channel-main">
                            <div id="video" className={videoAreaClass} />
                            <SFULayoutOverlay connection={connection.current} />
                            <TileChromeLayer connection={connection.current} />
                            <LiveCaptions enabled={captionsEnabled} />

                            {/* Layout mode switcher */}
                            <div className="layout-switcher">
                                <button
                                    className={`layout-btn${layoutMode === 'grid' ? ' active' : ''}`}
                                    onClick={() => setLayoutMode('grid')}
                                    title="Grid – show full video"
                                >
                                    <FontAwesomeIcon icon={faTh} />
                                </button>
                                <button
                                    className={`layout-btn${layoutMode === 'cover' ? ' active' : ''}`}
                                    onClick={() => setLayoutMode('cover')}
                                    title="Speaker – fill tiles"
                                >
                                    <FontAwesomeIcon icon={faUser} />
                                </button>
                            </div>
                        </div>

                        <FocusFilmstrip connection={connection.current} />
                    </div>

                    {!isMobile && sidePanelContent && (
                        <div className="channel-sidebar">
                            {sidePanelContent}
                        </div>
                    )}
                </div>

                <ChannelToolbar
                    connection={connection.current}
                    isMobileView={isMobile}
                    sidePanel={sidePanel}
                    setPanel={setSidePanel}
                    captionsEnabled={captionsEnabled}
                    onToggleCaptions={() => setCaptionsEnabled(p => !p)}
                    meetingStartTime={meetingStartTime.current}
                    onMuteAll={handleMuteAll}
                    unreadMessages={unreadMessages}
                    showEmojiPicker={showEmojiPicker}
                    onToggleEmojiPicker={() => setShowEmojiPicker(p => !p)}
                />

                {/* Emoji reaction floating layer */}
                {localMedia && (
                    <EmojiReactionLayer
                        connection={connection.current}
                        showPicker={showEmojiPicker}
                        onPickerClose={() => setShowEmojiPicker(false)}
                    />
                )}

                {/* Mobile side panel overlay */}
                {isMobile && sidePanelContent && (
                    <div className="mobile-panel-overlay" onClick={() => setSidePanel(null)}>
                        <div className="mobile-panel" onClick={e => e.stopPropagation()}>
                            {sidePanelContent}
                        </div>
                    </div>
                )}

                {/* Raise hand / mute-all notifications */}
                {chatNotifs.length > 0 && (
                    <div className="chat-message-notifs" aria-live="polite">
                        {chatNotifs.map(notification => (
                            <button
                                type="button"
                                key={notification.id}
                                className="chat-message-toast"
                                onClick={() => setSidePanel('chat')}
                            >
                                <span className="chat-message-toast-icon">
                                    <FontAwesomeIcon icon={faCommentDots} />
                                </span>
                                <span className="chat-message-toast-content">
                                    <strong>{notification.user}</strong>
                                    <span>{notification.message}</span>
                                </span>
                            </button>
                        ))}
                    </div>
                )}

                {raiseHandNotifs.length > 0 && (
                    <div className="raise-hand-notifs">
                        {raiseHandNotifs.map(n => {
                            const myAlias = localStorage.getItem('userAlias') || '';
                            const label = n.alias === myAlias ? 'You' : n.alias;
                            const isMuteAll = n.alias.includes('asked everyone');
                            return (
                                <div key={n.id} className="raise-hand-toast">
                                    <span className="raise-hand-emoji">{isMuteAll ? '🔇' : '✋'}</span>
                                    <div className="raise-hand-content">
                                        {isMuteAll ? (
                                            <span className="raise-hand-alias">{n.alias}</span>
                                        ) : (
                                            <>
                                                <span className="raise-hand-alias">{label}</span>
                                                <span className="raise-hand-text"> raised their hand</span>
                                            </>
                                        )}
                                    </div>
                                    <button
                                        className="raise-hand-dismiss"
                                        onClick={() => setRaiseHandNotifs(prev => prev.filter(x => x.id !== n.id))}
                                    >
                                        <FontAwesomeIcon icon={faTimes} />
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* Remote control request notification */}
                {controlRequest && (
                    <div className="control-request-toast">
                        <div className="control-request-icon">
                            <FontAwesomeIcon icon={faHandPaper} />
                        </div>
                        <div className="control-request-content">
                            <p className="control-request-title">Remote Control Request</p>
                            <p className="control-request-body">
                                <strong>{controlRequest.fromAlias}</strong> would like to control your screen
                            </p>
                        </div>
                        <div className="control-request-actions">
                            <button className="cr-btn cr-accept" title="Accept">
                                <FontAwesomeIcon icon={faCheck} />
                                <span>Accept</span>
                            </button>
                            <button className="cr-btn cr-deny" onClick={() => setControlRequest(null)} title="Deny">
                                <FontAwesomeIcon icon={faBan} />
                                <span>Deny</span>
                            </button>
                        </div>
                    </div>
                )}
            </div>

            <style>{`
                .channel-root {
                    height: 100vh;
                    height: 100dvh;
                    display: flex;
                    flex-direction: column;
                    background: var(--hx-bg);
                    overflow: hidden;
                    position: relative;
                    padding-top: env(safe-area-inset-top, 0px);
                }

                .channel-body {
                    flex: 1;
                    display: flex;
                    overflow: hidden;
                    gap: 0.65rem;
                    padding: 0.65rem 0.65rem calc(5.5rem + env(safe-area-inset-bottom, 0px));
                    min-height: 0;
                }

                .channel-stage {
                    flex: 1;
                    min-width: 0;
                    min-height: 0;
                    display: flex;
                    gap: 0.65rem;
                    overflow: hidden;
                }

                .channel-main {
                    flex: 1;
                    position: relative;
                    z-index: 1;
                    border-radius: var(--hx-radius);
                    overflow: hidden;
                    background: #000;
                    border: 1px solid var(--hx-line);
                    box-shadow: var(--hx-shadow);
                    min-width: 0;
                    min-height: 0;
                    isolation: isolate;
                }

                .video-grid {
                    width: 100%;
                    height: 100%;
                    position: absolute;
                    top: 0;
                    left: 0;
                    border-radius: inherit;
                    overflow: hidden;
                }

                .layout-switcher {
                    position: absolute;
                    bottom: 14px;
                    right: 14px;
                    z-index: 20;
                    display: flex;
                    background: rgba(255, 255, 255, 0.92);
                    border: 1px solid rgba(255, 255, 255, 0.6);
                    border-radius: 14px;
                    padding: 4px;
                    gap: 2px;
                    backdrop-filter: blur(14px);
                    -webkit-backdrop-filter: blur(14px);
                    box-shadow: var(--hx-shadow-sm);
                }

                .layout-btn {
                    width: 32px;
                    height: 32px;
                    border-radius: 10px;
                    border: none;
                    background: transparent;
                    color: var(--hx-muted);
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 0.8rem;
                    transition: background 0.15s, color 0.15s;
                }

                .layout-btn:hover { background: var(--hx-line-2); color: var(--hx-ink); }
                .layout-btn.active {
                    background: var(--hx-blue);
                    color: white;
                    box-shadow: 0 2px 10px rgba(37, 99, 235, 0.35);
                }

                .raise-hand-notifs {
                    position: fixed;
                    bottom: calc(100px + env(safe-area-inset-bottom, 0px));
                    left: max(1.25rem, env(safe-area-inset-left, 0px));
                    right: auto;
                    z-index: 50;
                    display: flex;
                    flex-direction: column-reverse;
                    gap: 0.5rem;
                    pointer-events: none;
                }

                .chat-message-notifs {
                    position: fixed;
                    top: calc(72px + env(safe-area-inset-top, 0px));
                    right: max(1.25rem, env(safe-area-inset-right, 0px));
                    z-index: 58;
                    display: flex;
                    flex-direction: column;
                    gap: 0.5rem;
                    pointer-events: none;
                }

                .chat-message-toast {
                    width: min(340px, calc(100vw - 2rem));
                    display: flex;
                    align-items: flex-start;
                    gap: 0.7rem;
                    padding: 0.75rem 0.85rem;
                    border: 1px solid var(--hx-line);
                    border-radius: 14px;
                    background: var(--hx-card);
                    color: var(--hx-ink-2);
                    box-shadow: var(--hx-shadow);
                    cursor: pointer;
                    pointer-events: auto;
                    text-align: left;
                    font-family: inherit;
                    animation: slideInRight 0.22s ease;
                }

                .chat-message-toast:hover {
                    border-color: var(--hx-blue-border);
                    background: var(--hx-blue-soft);
                }

                .chat-message-toast-icon {
                    width: 30px;
                    height: 30px;
                    border-radius: 9px;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    flex-shrink: 0;
                    background: var(--hx-blue-soft);
                    color: var(--hx-blue);
                }

                .chat-message-toast-content {
                    min-width: 0;
                    display: flex;
                    flex-direction: column;
                    gap: 0.18rem;
                }

                .chat-message-toast-content strong {
                    color: var(--hx-ink);
                    font-size: 0.76rem;
                    font-weight: 700;
                }

                .chat-message-toast-content span {
                    display: -webkit-box;
                    overflow: hidden;
                    color: var(--hx-ink-2);
                    font-size: 0.82rem;
                    line-height: 1.35;
                    -webkit-box-orient: vertical;
                    -webkit-line-clamp: 2;
                }

                .raise-hand-toast {
                    display: flex;
                    align-items: center;
                    gap: 0.75rem;
                    background: var(--hx-card);
                    border: 1px solid var(--hx-line);
                    border-radius: 14px;
                    padding: 0.7rem 0.9rem;
                    box-shadow: var(--hx-shadow);
                    animation: slideInLeft 0.25s ease;
                    pointer-events: auto;
                    max-width: min(300px, calc(100vw - 2rem));
                }

                @keyframes slideInLeft {
                    from { opacity: 0; transform: translateX(-10px); }
                    to   { opacity: 1; transform: translateX(0); }
                }

                .raise-hand-emoji { font-size: 1.15rem; flex-shrink: 0; }

                .raise-hand-content {
                    flex: 1;
                    font-size: 0.85rem;
                    line-height: 1.3;
                    min-width: 0;
                }

                .raise-hand-alias { font-weight: 650; color: var(--hx-ink); }
                .raise-hand-text { color: var(--hx-muted); }

                .raise-hand-dismiss {
                    background: transparent;
                    border: none;
                    color: var(--hx-muted);
                    cursor: pointer;
                    padding: 0.25rem;
                    font-size: 0.7rem;
                    flex-shrink: 0;
                    transition: color 0.15s;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    border-radius: 6px;
                }
                .raise-hand-dismiss:hover { color: var(--hx-ink); background: var(--hx-line-2); }

                .channel-sidebar {
                    width: min(340px, 34vw);
                    flex-shrink: 0;
                    display: flex;
                    flex-direction: column;
                    border-radius: var(--hx-radius);
                    overflow: hidden;
                    background: var(--hx-card);
                    border: 1px solid var(--hx-line);
                    box-shadow: var(--hx-shadow);
                }

                .mobile-panel-overlay {
                    position: fixed;
                    inset: 0;
                    background: rgba(15, 23, 42, 0.45);
                    backdrop-filter: blur(6px);
                    z-index: 60;
                    display: flex;
                    justify-content: flex-end;
                    animation: fadeIn 0.18s ease;
                    padding-top: env(safe-area-inset-top, 0px);
                }

                @keyframes fadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }

                .mobile-panel {
                    width: min(100%, 400px);
                    height: 100%;
                    background: var(--hx-card);
                    display: flex;
                    flex-direction: column;
                    position: relative;
                    border-left: 1px solid var(--hx-line);
                    animation: slideInRight 0.22s ease;
                    padding-bottom: env(safe-area-inset-bottom, 0px);
                }

                @keyframes slideInRight {
                    from { transform: translateX(24px); opacity: 0.6; }
                    to { transform: translateX(0); opacity: 1; }
                }

                .control-request-toast {
                    position: fixed;
                    top: calc(72px + env(safe-area-inset-top, 0px));
                    left: 50%;
                    transform: translateX(-50%);
                    z-index: 55;
                    background: var(--hx-card);
                    border: 1px solid var(--hx-line);
                    border-radius: var(--hx-radius);
                    padding: 1rem 1.15rem;
                    display: flex;
                    align-items: center;
                    gap: 1rem;
                    box-shadow: var(--hx-shadow);
                    min-width: 340px;
                    max-width: min(90vw, 520px);
                    animation: slideDown 0.25s ease;
                }

                @keyframes slideDown {
                    from { opacity: 0; transform: translateX(-50%) translateY(-12px); }
                    to   { opacity: 1; transform: translateX(-50%) translateY(0); }
                }

                .control-request-icon {
                    width: 42px; height: 42px;
                    border-radius: 12px;
                    background: #fffbeb;
                    color: #d97706;
                    display: flex; align-items: center; justify-content: center;
                    font-size: 1.05rem; flex-shrink: 0;
                    border: 1px solid #fde68a;
                }

                .control-request-content { flex: 1; min-width: 0; }

                .control-request-title {
                    font-size: 0.72rem; font-weight: 700;
                    text-transform: uppercase; letter-spacing: 0.07em;
                    color: var(--hx-muted); margin: 0 0 0.2rem;
                }

                .control-request-body { font-size: 0.9rem; color: var(--hx-ink-2); margin: 0; }
                .control-request-body strong { color: var(--hx-ink); }

                .control-request-actions { display: flex; gap: 0.5rem; flex-shrink: 0; }

                .cr-btn {
                    display: flex; align-items: center; gap: 0.375rem;
                    padding: 0.55rem 0.85rem;
                    border: none; border-radius: 10px;
                    font-size: 0.8rem; font-weight: 650;
                    cursor: pointer; transition: filter 0.15s, background 0.15s; white-space: nowrap;
                    font-family: inherit;
                }

                .cr-accept { background: var(--hx-success); color: white; }
                .cr-accept:hover { filter: brightness(1.08); }
                .cr-deny {
                    background: var(--hx-line-2);
                    color: var(--hx-muted);
                    border: 1px solid var(--hx-line);
                }
                .cr-deny:hover { background: var(--hx-line); color: var(--hx-ink-2); }

                @media (max-width: 1024px) {
                    .channel-sidebar { width: min(300px, 36vw); }
                }

                @media (max-width: 768px) {
                    .channel-body {
                        padding: 0.35rem 0.35rem calc(4.75rem + env(safe-area-inset-bottom, 0px));
                        gap: 0;
                    }
                    .channel-stage {
                        flex-direction: column;
                        gap: 0.35rem;
                    }
                    .channel-main { border-radius: 10px; }
                    .control-request-toast {
                        min-width: unset;
                        width: calc(100vw - 1.25rem);
                        flex-wrap: wrap;
                        gap: 0.75rem;
                        padding: 0.85rem 1rem;
                    }
                    .control-request-actions {
                        width: 100%;
                        display: grid;
                        grid-template-columns: 1fr 1fr;
                    }
                    .cr-btn { justify-content: center; }
                    .layout-switcher {
                        bottom: 8px;
                        right: 8px;
                        padding: 3px;
                        transform: scale(0.92);
                        transform-origin: bottom right;
                    }
                    .raise-hand-notifs {
                        bottom: calc(86px + env(safe-area-inset-bottom, 0px));
                        left: 0.65rem;
                        right: 0.65rem;
                    }
                    .raise-hand-toast { max-width: 100%; }
                    .chat-message-notifs {
                        top: calc(64px + env(safe-area-inset-top, 0px));
                        left: 0.65rem;
                        right: 0.65rem;
                    }
                    .chat-message-toast { width: 100%; }
                    .mobile-panel { width: min(100%, 100vw); }
                }

                @media (max-width: 480px) {
                    .channel-body { padding: 0.25rem 0.25rem calc(4.5rem + env(safe-area-inset-bottom, 0px)); }
                    .channel-main { border-radius: 8px; }
                    .layout-switcher { display: none; }
                }
            `}</style>
        </>
    );
}
